import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Star } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const STEPS = [
  {
    n: "01",
    t: "KORE Strength A",
    d: "Full-body strength training with an emphasis on squatting, pushing and core stability. Every exercise is coached and adjusted to your ability.",
  },
  {
    n: "02",
    t: "KORE Strength B",
    d: "A second full-body session with an emphasis on hinging, pulling and carrying — giving your entire body the strength it needs for everyday life.",
  },
];

const REASONS = [
  "You know you should exercise but struggle to stay consistent.",
  "You don't feel comfortable in conventional gyms.",
  "You want someone to tell you what to do.",
  "You're returning after years away from structured training.",
  "You want to remain strong, fit and capable as you get older.",
  "You want to enjoy training rather than endure it.",
];

export default function HowItWorks() {
  const [testimonial, setTestimonial] = useState(null);
  useEffect(() => {
    api.get("/testimonials")
      .then((r) => setTestimonial((r.data || [])[0] || null))
      .catch(() => setTestimonial(null));
  }, []);

  return (
    <div className="bg-kore-warm">
      <Seo
        title="How KORE Works — coached strength & fitness for 40+"
        description="A simple weekly plan: two coached KORE Strength sessions a week, on Monday & Wednesday or Tuesday & Thursday. Built around real people in their 40s, 50s, 60s and beyond."
        path="/how-it-works"
      />

      {/* Hero */}
      <section className="bg-kore-forest text-kore-warm pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container-kore max-w-4xl">
          <span className="text-[11px] uppercase tracking-[0.28em] text-kore-light">How KORE works</span>
          <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em] mt-6">
            Three sessions.<br/>
            <span className="italic text-kore-light">One simple plan.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-kore-warm/85 leading-[1.6] max-w-2xl">
            Most gyms give you equipment and leave the plan up to you. At KORE, every session is coached and every exercise can be adjusted to your experience, ability and current fitness.
          </p>
        </div>
      </section>

      {/* The plan */}
      <section className="py-24 md:py-32">
        <div className="container-kore max-w-5xl">
          <div className="grid md:grid-cols-3 gap-x-12 gap-y-14">
            {STEPS.map((s) => (
              <div key={s.n}>
                <div className="text-kore-gold text-sm font-medium">{s.n}</div>
                <h2 className="mt-4 font-display text-3xl text-kore-forest leading-tight">{s.t}</h2>
                <p className="mt-4 text-base text-kore-ink/80 leading-[1.6]">{s.d}</p>
              </div>
            ))}
          </div>

          {/* Coach guidance line */}
          <p
            data-testid="howitworks-coach-guidance"
            className="mt-16 text-base md:text-lg text-kore-forest/90 leading-[1.6] max-w-3xl"
          >
            You won't be left to figure it out. Your coach demonstrates every exercise, watches your technique and adjusts the session around your experience, ability and any physical limitations.
          </p>
        </div>
      </section>

      {/* Real training image */}
      <section data-testid="howitworks-training-image" className="pb-24 md:pb-32">
        <div className="container-kore max-w-5xl">
          <figure className="rounded-3xl overflow-hidden shadow-lift">
            <img
              src="/gallery/kore-training-1.webp"
              alt="KORE members being coached through a strength session at Kore Wellbeing, Bowness-on-Windermere"
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </figure>
          <figcaption className="mt-4 text-[11px] uppercase tracking-[0.24em] text-kore-ink/55">
            Inside KORE · a coached session in progress · Bowness-on-Windermere
          </figcaption>
        </div>
      </section>

      {/* Compact verified member testimonial */}
      {testimonial && (
        <section data-testid="howitworks-testimonial" className="pb-24 md:pb-32">
          <div className="container-kore max-w-3xl">
            <div className="rounded-3xl bg-white border border-kore-gold/20 p-8 md:p-12 shadow-soft">
              <div className="flex gap-0.5" aria-hidden>
                {Array.from({ length: testimonial.rating || 5 }).map((_, i) => (
                  <Star key={i} size={14} className="text-kore-gold fill-kore-gold" />
                ))}
              </div>
              <blockquote className="mt-6 font-display text-2xl md:text-3xl text-kore-forest leading-[1.35] italic">
                &ldquo;{testimonial.short_quote || testimonial.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-kore-gold/15 flex items-center gap-4">
                {testimonial.image_url ? (
                  <img
                    src={testimonial.image_url}
                    alt={testimonial.name}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="w-12 h-12 rounded-full object-cover border border-kore-gold/25"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="w-12 h-12 rounded-full bg-kore-forest text-kore-warm flex items-center justify-center text-sm font-medium"
                  >
                    {(testimonial.name || "K").slice(0, 1)}
                  </div>
                )}
                <div>
                  <div className="font-display text-kore-forest text-lg">{testimonial.name}</div>
                  {testimonial.role && (
                    <div className="text-[11px] uppercase tracking-[0.2em] text-kore-ink/55 mt-1">
                      {testimonial.role}
                    </div>
                  )}
                </div>
              </figcaption>
            </div>
          </div>
        </section>
      )}

      {/* Who it's for */}
      <section className="bg-kore-light/30 py-24 md:py-32">
        <div className="container-kore max-w-4xl">
          <span className="eyebrow">The right fit</span>
          <h2 className="display-2 mt-4 text-kore-forest">KORE might be for you if&hellip;</h2>
          <ul className="mt-12 space-y-5">
            {REASONS.map((r) => (
              <li key={r} className="flex gap-4 text-base md:text-lg text-kore-ink/85 leading-[1.6]">
                <span className="mt-2 rounded-full bg-kore-forest text-kore-warm p-1 shrink-0"><Check size={12} /></span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA — single conversion action */}
      <section className="bg-kore-forest text-kore-warm py-28 md:py-36 text-center">
        <div className="container-kore max-w-2xl">
          <h2 className="display-2">Ready to start?</h2>
          <p className="mt-6 text-base md:text-lg text-kore-warm/85 leading-[1.6]">
            The 30 Day Coached Trial is the easiest way to experience KORE. Two coached Strength sessions each week — Monday & Wednesday or Tuesday & Thursday — from day one.
          </p>
          <Link to="/30-day-trial" data-testid="how-it-works-cta" className="btn-gold mt-10 min-h-[48px]">
            Start your 30 Day Trial <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
